import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, User, ChevronDown, ChevronUp, Download } from 'lucide-react';

interface Consultation {
  id: string;
  date: string;
  symptoms: string[];
  diagnosis?: string;
  doctorName?: string;
  status: string;
}

interface PreviousFile {
  id: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  requestId: string;
  url: string;
  description?: string;
}

interface PatientHistoryProps {
  email: string;
  onClose?: () => void;
}

const PatientHistory: React.FC<PatientHistoryProps> = ({ email, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [files, setFiles] = useState<PreviousFile[]>([]);
  const [expandedConsultation, setExpandedConsultation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchPatientHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';

      // Fetch consultation history
      const historyResponse = await fetch(
        `${apiUrl}/patients/history?gmail=${encodeURIComponent(email)}`
      );

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setConsultations(historyData.consultations || []);
      }

      // Fetch previous files
      const filesResponse = await fetch(
        `${apiUrl}/patients/files?gmail=${encodeURIComponent(email)}`
      );

      if (filesResponse.ok) {
        const filesData = await filesResponse.json();
        setFiles(filesData.files || []);
      }
    } catch (err) {
      console.error('Error fetching patient history:', err);
      setError('Failed to load your history. You can still continue with your booking.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    fetchPatientHistory();
  }, [fetchPatientHistory]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    return '📎';
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="w-12 h-12 border-4 border-[#38BDF8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#334155]/60">Loading your history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
        <p className="text-sm text-yellow-800">{error}</p>
      </div>
    );
  }

  if (consultations.length === 0 && files.length === 0) {
    return (
      <div className="p-6 text-center bg-[#F0F9FF] rounded-xl">
        <User className="w-12 h-12 text-[#38BDF8] mx-auto mb-3" />
        <p className="text-[#334155] font-medium">No previous consultations found</p>
        <p className="text-sm text-[#334155]/60 mt-1">This will be your first consultation with us</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-[#334155]">Your Consultation History</h4>
        {onClose && (
          <button
            onClick={onClose}
            className="text-sm text-[#38BDF8] hover:text-[#0EA5E9]"
          >
            Hide
          </button>
        )}
      </div>

      {/* Previous Consultations */}
      {consultations.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-sm font-medium text-[#334155]">
            Previous Consultations ({consultations.length})
          </h5>
          {consultations.map((consultation) => (
            <div
              key={consultation.id}
              className="border border-[#334155]/20 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setExpandedConsultation(
                  expandedConsultation === consultation.id ? null : consultation.id
                )}
                className="w-full p-4 flex items-center justify-between hover:bg-[#F0F9FF] transition-colors"
              >
                <div className="flex items-center gap-3 text-left">
                  <Calendar className="w-5 h-5 text-[#38BDF8] flex-shrink-0" />
                  <div>
                    <p className="font-medium text-[#334155]">
                      {formatDate(consultation.date)}
                    </p>
                    <p className="text-sm text-[#334155]/60">
                      {consultation.symptoms.slice(0, 2).join(', ')}
                      {consultation.symptoms.length > 2 && ` +${consultation.symptoms.length - 2} more`}
                    </p>
                  </div>
                </div>
                {expandedConsultation === consultation.id ? (
                  <ChevronUp className="w-5 h-5 text-[#334155]/40" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#334155]/40" />
                )}
              </button>

              {expandedConsultation === consultation.id && (
                <div className="px-4 pb-4 space-y-3 bg-[#F0F9FF]/50">
                  <div>
                    <p className="text-xs font-medium text-[#334155]/60 mb-1">Symptoms</p>
                    <div className="flex flex-wrap gap-2">
                      {consultation.symptoms.map((symptom, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-white border border-[#38BDF8]/20 rounded-lg text-xs text-[#334155]"
                        >
                          {symptom}
                        </span>
                      ))}
                    </div>
                  </div>

                  {consultation.diagnosis && (
                    <div>
                      <p className="text-xs font-medium text-[#334155]/60 mb-1">Diagnosis</p>
                      <p className="text-sm text-[#334155]">{consultation.diagnosis}</p>
                    </div>
                  )}

                  {consultation.doctorName && (
                    <div>
                      <p className="text-xs font-medium text-[#334155]/60 mb-1">Doctor</p>
                      <p className="text-sm text-[#334155]">{consultation.doctorName}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-medium text-[#334155]/60 mb-1">Status</p>
                    <span className={`inline-block px-2 py-1 rounded-lg text-xs ${
                      consultation.status === 'completed' 
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {consultation.status}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Previous Files */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-sm font-medium text-[#334155]">
            Previously Uploaded Files ({files.length})
          </h5>
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 bg-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl"
              >
                <span className="text-2xl">{getFileIcon(file.mimeType)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#334155] truncate">
                    {file.filename}
                  </p>
                  <p className="text-xs text-[#334155]/60">
                    {formatFileSize(file.fileSize)} • {formatDate(file.uploadedAt)}
                  </p>
                  {file.description && (
                    <p className="text-xs text-[#334155]/50 mt-1">{file.description}</p>
                  )}
                </div>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 p-2 hover:bg-[#38BDF8]/10 rounded-lg transition-colors"
                  title="Download file"
                >
                  <Download className="w-4 h-4 text-[#38BDF8]" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
        <p className="text-xs text-green-800">
          ✓ Your previous medical information will be available to your doctor during this consultation
        </p>
      </div>
    </div>
  );
};

export default PatientHistory;
